import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { Country, Shipment } from "../types";
import { PACKAGE_TYPES } from "../constants/formOptions";

/**
 * Shipment Order (SO) PDF — available for every transport mode. Every field is
 * pulled from existing shipment data (no new user input), per the SO feature
 * spec. Layout mirrors the other LiveCargo document generators (BOL / AWB):
 * A4, bordered cells with gray section labels.
 */

function safe<T>(fn: () => T, fallback = "-"): string {
  try {
    const val = fn();
    return val !== undefined && val !== null && val !== ""
      ? String(val)
      : fallback;
  } catch {
    return fallback;
  }
}

function wrapText(
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number,
  maxLines = 6,
): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

const MODE_LABEL: Record<string, string> = {
  ROAD: "Road freight",
  SEA: "Sea freight",
  AIR: "Air freight",
};

const packageLabel = (code?: string) =>
  PACKAGE_TYPES.find((p) => p.value === code)?.label || code || "";

const fmtDate = (d?: string | Date) => {
  if (!d) return "-";
  const s = typeof d === "string" ? d : d.toISOString();
  return s.split("T")[0];
};

export async function createShipmentOrderPdf(
  data: Shipment,
  countries: Country[],
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle(
    `Shipment Order${data.shipmentNumber ? ` - ${data.shipmentNumber}` : ""}`,
  );
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject("Shipment Order");
  pdfDoc.setCreationDate(new Date());

  let page: PDFPage = pdfDoc.addPage([595, 842]); // A4
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const black = rgb(0.07, 0.07, 0.1);
  const gray = rgb(0.42, 0.45, 0.5);
  const border = rgb(0.12, 0.16, 0.22);
  const bandBg = rgb(0.91, 0.94, 0.98);

  const margin = 36;
  const innerWidth = width - 2 * margin;
  let yPos = height - margin;

  const countryOptions = (countries ?? []).flat().map((c) => ({
    value: c._id,
    label: c.name,
  }));
  const countryName = (codeOrName: any): string => {
    if (!codeOrName) return "";
    const code = typeof codeOrName === "string" ? codeOrName : codeOrName?._id;
    if (!code) return "";
    const found = countryOptions.find(
      (c) => c.value?.toLowerCase?.() === code.toLowerCase?.(),
    );
    return found?.label || (typeof codeOrName === "string" ? "" : "");
  };

  // Address as a single line: "street, postal city, country".
  const addressLine = (party: any): string => {
    if (!party) return "";
    const addr = party.address ?? {};
    return [
      addr.street,
      [addr.postalCode, addr.city, addr.state].filter(Boolean).join(" "),
      countryName(addr.country),
    ]
      .filter(Boolean)
      .join(", ");
  };

  const contactLine = (party: any): string =>
    [safe(() => party?.emailAddress, ""), safe(() => party?.phoneNumber, "")]
      .filter((v) => v && v !== "-")
      .join("  |  ");

  // Forwarder is mode-dependent: SEA/AIR carry it inside their details block,
  // ROAD on the top-level forwarder field.
  const forwarder =
    data.transportMode === "SEA"
      ? data.seaDetails?.forwarder
      : data.transportMode === "AIR"
        ? data.airDetails?.forwarder
        : data.forwarder;

  const transport =
    data.transportMode === "SEA"
      ? {
          pol: data.seaDetails?.portOfLoading,
          pod: data.seaDetails?.portOfDischarge,
          etd: data.seaDetails?.etd,
        }
      : data.transportMode === "AIR"
        ? {
            pol: data.airDetails?.airportOfDeparture,
            pod: data.airDetails?.airportOfArrival,
            etd: data.airDetails?.etd,
          }
        : { pol: undefined, pod: undefined, etd: undefined };

  // ── drawing helpers ──────────────────────────────────────────────────────────
  const drawCell = (
    x: number,
    top: number,
    w: number,
    h: number,
    label: string,
    valueLines: string[],
    opts: { valueSize?: number; valueBold?: boolean } = {},
  ) => {
    page.drawRectangle({
      x,
      y: top - h,
      width: w,
      height: h,
      borderColor: border,
      borderWidth: 1,
    });
    if (label)
      page.drawText(label.toUpperCase(), {
        x: x + 6,
        y: top - 12,
        size: 7,
        font: boldFont,
        color: gray,
      });
    const size = opts.valueSize ?? 9;
    const vfont = opts.valueBold ? boldFont : font;
    const maxW = w - 12;
    const bottom = top - h + 5;
    let vy = top - (label ? 24 : 14);
    // Wrap every value line to the cell width so long text (addresses, etc.)
    // never spills across the cell border.
    for (const raw of valueLines) {
      if (!raw) continue;
      for (const line of wrapText(raw, vfont, size, maxW, 4)) {
        if (vy < bottom) return;
        page.drawText(line, {
          x: x + 6,
          y: vy,
          size,
          font: vfont,
          color: black,
        });
        vy -= size + 3;
      }
    }
  };

  // Section title bar.
  const sectionBar = (title: string) => {
    const h = 16;
    page.drawRectangle({
      x: margin,
      y: yPos - h,
      width: innerWidth,
      height: h,
      color: bandBg,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText(title.toUpperCase(), {
      x: margin + 6,
      y: yPos - 11,
      size: 8,
      font: boldFont,
      color: black,
    });
    yPos -= h;
  };

  const ensure = (needed: number) => {
    if (yPos - needed < margin) {
      page = pdfDoc.addPage([595, 842]);
      yPos = height - margin;
    }
  };

  // ── Header band · Shipper | Shipment Order meta ──────────────────────────────
  const headerH = 78;
  const halfW = innerWidth / 2;
  page.drawRectangle({
    x: margin,
    y: yPos - headerH,
    width: innerWidth,
    height: headerH,
    color: bandBg,
    borderColor: border,
    borderWidth: 1,
  });
  // Left — shipper (pickup)
  let hy = yPos - 16;
  page.drawText(safe(() => data.pickup?.companyName), {
    x: margin + 8,
    y: hy,
    size: 13,
    font: boldFont,
    color: black,
  });
  hy -= 14;
  // Keep the shipper block on the left ~58% so it never runs under the
  // right-aligned Shipment Order meta column.
  const headerMaxW = innerWidth * 0.58;
  for (const raw of [addressLine(data.pickup), contactLine(data.pickup)]) {
    if (!raw) continue;
    for (const line of wrapText(raw, font, 8, headerMaxW, 2)) {
      page.drawText(line, { x: margin + 8, y: hy, size: 8, font, color: gray });
      hy -= 11;
    }
  }
  const badge = `[${MODE_LABEL[data.transportMode ?? "ROAD"] ?? "Road freight"}${
    data.loadType ? ` -- ${data.loadType}` : ""
  }]`;
  page.drawText(badge, { x: margin + 8, y: hy, size: 8, font, color: black });

  // Right — Shipment Order meta (right-aligned)
  const rightEdge = width - margin - 8;
  const drawRight = (text: string, y: number, size: number, bold = false) => {
    const f = bold ? boldFont : font;
    const w = f.widthOfTextAtSize(text, size);
    page.drawText(text, { x: rightEdge - w, y, size, font: f, color: black });
  };
  drawRight("Shipment Order", yPos - 18, 15, true);
  drawRight(`No.  ${safe(() => data.shipmentNumber)}`, yPos - 36, 9);
  drawRight(`Date:  ${fmtDate(new Date())}`, yPos - 49, 9);
  drawRight(`Ref:  ${safe(() => data.referenceNumber, "-")}`, yPos - 62, 9);
  yPos -= headerH + 6;

  // ── Ordered by (shipper) | To (forwarder) ────────────────────────────────────
  const partyH = 78;
  ensure(partyH);
  drawCell(margin, yPos, halfW, partyH, "Ordered by (Shipper)", [
    safe(() => data.pickup?.companyName),
    addressLine(data.pickup),
    contactLine(data.pickup),
  ].filter((l) => l && l !== "-"), { valueBold: false });
  drawCell(margin + halfW, yPos, halfW, partyH, "To (Forwarder)", [
    safe(() => forwarder?.companyName),
    safe(() => forwarder?.contactPerson, ""),
    [safe(() => forwarder?.email, ""), safe(() => forwarder?.phone, "")]
      .filter((v) => v && v !== "-")
      .join("  |  "),
  ].filter((l) => l && l !== "-"));
  yPos -= partyH + 6;

  // ── Transport details ────────────────────────────────────────────────────────
  sectionBar("Transport details");
  const tH = 40;
  ensure(tH);
  const sixth = innerWidth / 6;
  const incoterm = [safe(() => data.incoterms, ""), safe(() => data.place, "")]
    .filter((v) => v && v !== "-")
    .join(" / ");
  const cells: Array<[string, string]> = [
    ["Mode", MODE_LABEL[data.transportMode ?? "ROAD"] ?? "Road freight"],
    ["Service Type", safe(() => data.loadType)],
    ["Incoterm", incoterm || "-"],
    ["Port of Loading", safe(() => transport.pol)],
    ["Port of Discharge", safe(() => transport.pod)],
    ["Requested ETD", fmtDate(transport.etd)],
  ];
  cells.forEach(([label, value], i) => {
    drawCell(margin + sixth * i, yPos, sixth, tH, label, [value], {
      valueSize: 8,
    });
  });
  yPos -= tH + 6;

  // ── Pickup & delivery ────────────────────────────────────────────────────────
  sectionBar("Pickup & delivery");
  const pdH = 78;
  ensure(pdH);
  drawCell(margin, yPos, halfW, pdH, "Pickup", [
    safe(() => data.pickup?.companyName),
    addressLine(data.pickup),
    `Date: ${fmtDate(data.pickup?.date)}${
      data.pickup?.time ? `  |  ${data.pickup.time}` : ""
    }`,
  ].filter((l) => l && l !== "-"));
  drawCell(margin + halfW, yPos, halfW, pdH, "Delivery", [
    safe(() => data.delivery?.companyName),
    addressLine(data.delivery),
    `ETA: ${fmtDate(data.delivery?.date)}`,
  ].filter((l) => l && l !== "-"));
  yPos -= pdH + 6;

  // ── Consignee ────────────────────────────────────────────────────────────────
  sectionBar("Consignee");
  const cH = 50;
  ensure(cH);
  drawCell(
    margin,
    yPos,
    innerWidth,
    cH,
    "",
    [
      safe(() => data.delivery?.companyName),
      addressLine(data.delivery),
      data.delivery?.registerationCode
        ? `Reg. ${data.delivery.registerationCode}`
        : "",
    ].filter((v) => v && v !== "-"),
  );
  yPos -= cH + 6;

  // ── Cargo table ──────────────────────────────────────────────────────────────
  sectionBar("Cargo");
  const cols = [
    { label: "Qty", w: innerWidth * 0.08 },
    { label: "Package type", w: innerWidth * 0.18 },
    { label: "Weight (kg)", w: innerWidth * 0.14 },
    { label: "Volume (m³)", w: innerWidth * 0.13 },
    { label: "LDM", w: innerWidth * 0.1 },
    { label: "Description / marks", w: innerWidth * 0.37 },
  ];
  const headH = 16;
  ensure(headH);
  let cx = margin;
  for (const col of cols) {
    page.drawRectangle({
      x: cx,
      y: yPos - headH,
      width: col.w,
      height: headH,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText(col.label, {
      x: cx + 4,
      y: yPos - 11,
      size: 7,
      font: boldFont,
      color: gray,
    });
    cx += col.w;
  }
  yPos -= headH;

  const items = data.cargoItems ?? [];
  let totQty = 0;
  let totWeight = 0;
  let totVolume = 0;
  let totLdm = 0;
  for (const item of items) {
    const descText = [item.description, item.marks].filter(Boolean).join(" — ");
    const descLines = wrapText(descText, font, 8, cols[5].w - 8, 2);
    const rowH = Math.max(16, 6 + Math.max(1, descLines.length) * 10);
    ensure(rowH);
    const values = [
      String(item.quantity ?? ""),
      packageLabel(item.type),
      item.weightKg != null ? String(item.weightKg) : "",
      item.volumeM3 != null ? Number(item.volumeM3).toFixed(2) : "",
      item.ldm != null ? Number(item.ldm).toFixed(2) : "",
    ];
    cx = margin;
    cols.forEach((col, i) => {
      page.drawRectangle({
        x: cx,
        y: yPos - rowH,
        width: col.w,
        height: rowH,
        borderColor: border,
        borderWidth: 1,
      });
      if (i < 5) {
        page.drawText(values[i], {
          x: cx + 4,
          y: yPos - 11,
          size: 8,
          font,
          color: black,
        });
      } else {
        let dy = yPos - 11;
        for (const l of descLines.length ? descLines : ["-"]) {
          page.drawText(l, { x: cx + 4, y: dy, size: 8, font, color: black });
          dy -= 10;
        }
      }
      cx += col.w;
    });
    yPos -= rowH;
    totQty += Number(item.quantity) || 0;
    totWeight += Number(item.weightKg) || 0;
    totVolume += Number(item.volumeM3) || 0;
    totLdm += Number(item.ldm) || 0;
  }

  // Totals row
  const totH = 16;
  ensure(totH);
  const totals = [
    String(totQty || ""),
    "Totals",
    totWeight ? `${totWeight.toFixed(2)} kg` : "",
    totVolume ? `${totVolume.toFixed(2)} m³` : "",
    totLdm ? totLdm.toFixed(2) : "",
    "",
  ];
  cx = margin;
  cols.forEach((col, i) => {
    page.drawRectangle({
      x: cx,
      y: yPos - totH,
      width: col.w,
      height: totH,
      color: bandBg,
      borderColor: border,
      borderWidth: 1,
    });
    if (totals[i])
      page.drawText(totals[i], {
        x: cx + 4,
        y: yPos - 11,
        size: 8,
        font: boldFont,
        color: black,
      });
    cx += col.w;
  });
  yPos -= totH + 6;

  // ── Remarks / instructions ───────────────────────────────────────────────────
  sectionBar("Remarks / instructions");
  const remarkLines = wrapText(
    safe(() => data.additionalNotes, ""),
    font,
    9,
    innerWidth - 12,
    5,
  );
  const rH = Math.max(30, 12 + remarkLines.length * 12);
  ensure(rH);
  drawCell(margin, yPos, innerWidth, rH, "", remarkLines.length ? remarkLines : ["-"]);
  yPos -= rH + 6;

  // ── Signature block ──────────────────────────────────────────────────────────
  const sigH = 54;
  ensure(sigH + 24);
  drawCell(margin, yPos, halfW, sigH, "Authorised by (Shipper)", [
    safe(() => data.pickup?.companyName),
    [safe(() => data.pickup?.address?.city, ""), safe(() => data.pickup?.emailAddress, "")]
      .filter((v) => v && v !== "-")
      .join("  |  "),
  ].filter((l) => l && l !== "-"));
  drawCell(margin + halfW, yPos, halfW, sigH, "Accepted by (Forwarder)", []);
  // signature line on the forwarder cell
  page.drawText("__________________________", {
    x: margin + halfW + 8,
    y: yPos - 30,
    size: 9,
    font,
    color: gray,
  });
  page.drawText("Date: ______________", {
    x: margin + halfW + 8,
    y: yPos - 44,
    size: 8,
    font,
    color: gray,
  });
  yPos -= sigH + 16;

  // ── Footer ───────────────────────────────────────────────────────────────────
  page.drawText("Generated by LiveCargo · livecargo.io", {
    x: margin,
    y: 24,
    size: 8,
    font,
    color: gray,
  });
  const footRight = `${safe(() => data.shipmentNumber)}  |  ${fmtDate(new Date())}`;
  const frW = font.widthOfTextAtSize(footRight, 8);
  page.drawText(footRight, {
    x: width - margin - frW,
    y: 24,
    size: 8,
    font,
    color: gray,
  });

  return await pdfDoc.save();
}

export async function downloadShipmentOrderPdf(
  data: Shipment,
  countries: Country[],
  filename?: string,
): Promise<void> {
  const pdfBytes = await createShipmentOrderPdf(data, countries);
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename ||
    `Shipment-Order${data.shipmentNumber ? `-${data.shipmentNumber}` : ""}.pdf`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
